/**
 * 피처 플래그 평가 로직
 *
 * 사용자 정보를 기반으로 피처 플래그의 활성화 여부를 결정합니다.
 */
import {
    FEATURE_FLAGS,
    FeatureFlagName,
    FeatureFlagRule,
    UserRole
} from "constants/featureFlags";

import { murmurhash } from "./murmurhash";

/** 사용자 정보 타입 */
export interface FeatureFlagUser {
    /** 사용자 ID (해시 계산에 사용) */
    id: string;
    /** 사용자 역할 */
    role: UserRole;
}

/** 32비트 unsigned integer 최대값 */
const MAX_UINT_32 = 4294967295;

/**
 * 사용자가 특정 피처 플래그를 볼 수 있는지 확인
 *
 * @param featureName 피처 플래그 이름
 * @param user 사용자 정보 (null이면 guest로 처리)
 * @returns 피처가 활성화되었는지 여부
 *
 * @example
 * ```tsx
 * const enabled = canViewFeature("CHRISTMAS_EVENT", user);
 * if (enabled) {
 *   return <ChristmasEvent />;
 * }
 * ```
 */
export function canViewFeature(
    featureName: FeatureFlagName,
    user: FeatureFlagUser | null
): boolean {
    const rules = FEATURE_FLAGS[featureName];

    // 플래그가 정의되지 않은 경우 비활성화
    if (rules == null) {
        return false;
    }

    // Boolean 플래그인 경우
    if (typeof rules === "boolean") {
        return rules;
    }

    // 사용자가 없으면 guest로 처리
    const effectiveUser: FeatureFlagUser = user ?? {
        id: "guest",
        role: "guest"
    };

    // 단일 규칙인 경우
    if (!Array.isArray(rules)) {
        return checkRule(rules, featureName, effectiveUser);
    }

    // 여러 규칙 중 하나라도 만족하면 활성화 (OR 조건)
    return rules.some((rule) => checkRule(rule, featureName, effectiveUser));
}

/**
 * Hasura에서 가져온 플래그 데이터로 피처 활성화 여부 확인
 *
 * @param flagData Hasura 플래그 데이터
 * @param user 사용자 정보 (null이면 guest로 처리)
 * @returns 피처가 활성화되었는지 여부
 *
 * @example
 * ```tsx
 * const flagData = flags.get("CHRISTMAS_EVENT");
 * const enabled = canViewFeatureFromData(flagData, user);
 * ```
 */
export function canViewFeatureFromData(
    flagData: {
        name: string;
        enabled: boolean;
        rules: any | null;
    },
    user: FeatureFlagUser | null
): boolean {
    // 플래그가 비활성화되어 있으면 즉시 false 반환
    if (!flagData.enabled) {
        return false;
    }

    // 규칙이 없으면 enabled 값만 사용 (simple boolean flag)
    if (flagData.rules == null) {
        return flagData.enabled;
    }

    // 사용자가 없으면 guest로 처리
    const effectiveUser: FeatureFlagUser = user ?? {
        id: "guest",
        role: "guest"
    };

    const rules = flagData.rules;

    // 단일 규칙인 경우
    if (!Array.isArray(rules)) {
        return checkRuleFromData(rules, flagData.name, effectiveUser);
    }

    // 여러 규칙 중 하나라도 만족하면 활성화 (OR 조건)
    return rules.some((rule: any) =>
        checkRuleFromData(rule, flagData.name, effectiveUser)
    );
}

/**
 * Hasura 데이터로부터 단일 규칙을 평가
 *
 * @param rule 규칙 객체
 * @param featureName 피처 이름
 * @param user 사용자 정보
 * @returns 규칙을 만족하는지 여부
 */
function checkRuleFromData(
    rule: any,
    featureName: string,
    user: FeatureFlagUser
): boolean {
    const { userRoles, percentageOfUsers } = rule;

    // 역할 검증과 퍼센트 검증 모두 통과해야 함 (AND 조건)
    return (
        userHasValidRole(userRoles, user.role) &&
        userIsWithinPercentageFromData(featureName, percentageOfUsers, user.id)
    );
}

/**
 * Hasura 데이터로부터 퍼센트 범위 확인
 *
 * @param featureName 피처 이름
 * @param allowedPercent 허용 퍼센트
 * @param userId 사용자 ID
 * @returns 범위 내 여부
 */
function userIsWithinPercentageFromData(
    featureName: string,
    allowedPercent: number | undefined,
    userId: string
): boolean {
    // 퍼센트 제한이 없으면 모든 사용자 허용
    if (allowedPercent == null) {
        return true;
    }

    // 피처 이름과 사용자 ID를 조합하여 해시 생성
    const hash = murmurhash(`${featureName}-${userId}`);

    // 해시값을 0.0 ~ 1.0 사이의 값으로 정규화
    const normalized = hash / MAX_UINT_32;

    // 정규화된 값이 허용된 퍼센트보다 작으면 활성화
    return normalized < allowedPercent;
}

/**
 * 단일 규칙을 평가
 *
 * @param rule 피처 플래그 규칙
 * @param featureName 피처 플래그 이름
 * @param user 사용자 정보
 * @returns 규칙을 만족하는지 여부
 */
function checkRule(
    rule: FeatureFlagRule,
    featureName: FeatureFlagName,
    user: FeatureFlagUser
): boolean {
    const { userRoles, percentageOfUsers } = rule;

    // 역할 검증과 퍼센트 검증 모두 통과해야 함 (AND 조건)
    return (
        userHasValidRole(userRoles, user.role) &&
        userIsWithinPercentage(featureName, percentageOfUsers, user.id)
    );
}

/**
 * 사용자가 허용된 역할을 가지고 있는지 확인
 *
 * @param allowedRoles 허용된 역할 목록 (undefined면 모든 역할 허용)
 * @param userRole 사용자 역할
 * @returns 역할이 허용되는지 여부
 */
function userHasValidRole(
    allowedRoles: UserRole[] | undefined,
    userRole: UserRole
): boolean {
    // 역할 제한이 없으면 모든 역할 허용
    if (allowedRoles == null) {
        return true;
    }

    // 사용자 역할이 허용된 역할 목록에 포함되어 있는지 확인
    return allowedRoles.includes(userRole);
}

/**
 * 사용자가 퍼센트 범위 내에 있는지 확인
 *
 * MurmurHash를 사용하여 사용자 ID와 피처 이름을 조합한 해시값을 생성하고,
 * 이를 통해 일관된 그룹 할당을 보장합니다.
 *
 * @param featureName 피처 플래그 이름
 * @param allowedPercent 허용된 퍼센트 (0.0 ~ 1.0, undefined면 모든 사용자 허용)
 * @param userId 사용자 ID
 * @returns 사용자가 퍼센트 범위 내에 있는지 여부
 *
 * @example
 * ```
 * // 30% 롤아웃
 * allowedPercent = 0.3
 *
 * // userId "user123"과 featureName "NEW_FEATURE"를 조합하여 해시 생성
 * hash = murmurhash("NEW_FEATURE-user123")
 * normalized = hash / MAX_UINT_32  // 0.0 ~ 1.0 사이의 값
 *
 * // normalized 값이 0.3보다 작으면 활성화
 * // 동일한 userId는 항상 동일한 normalized 값을 가지므로
 * // 사용자는 항상 동일한 그룹에 할당됨
 * ```
 */
function userIsWithinPercentage(
    featureName: FeatureFlagName,
    allowedPercent: number | undefined,
    userId: string
): boolean {
    // 퍼센트 제한이 없으면 모든 사용자 허용
    if (allowedPercent == null) {
        return true;
    }

    // 피처 이름과 사용자 ID를 조합하여 해시 생성
    // 이를 통해 동일한 사용자는 항상 동일한 그룹에 할당됨
    const hash = murmurhash(`${featureName}-${userId}`);

    // 해시값을 0.0 ~ 1.0 사이의 값으로 정규화
    const normalized = hash / MAX_UINT_32;

    // 정규화된 값이 허용된 퍼센트보다 작으면 활성화
    return normalized < allowedPercent;
}
